import React from 'react';
import { GameController, Trophy, Cpu, ArrowRight, Star, HardDrive } from '@phosphor-icons/react';

const MmcDashboard = ({ games, onSelectGame }) => {
    return (
        <div className="mmc-dashboard">
            <header className="mmc-dashboard__hero">
                <h1 className="mmc-dashboard__title">
                    PLAY WITHOUT <br />
                    <span className="mmc-text-primary">LIMITS</span>
                </h1>
                <p className="mmc-dashboard__subtitle">
                    A lightweight collection of retro-technical games built for the Madmoco ecosystem.
                    Zero lag. No ads. Pure skill.
                </p>
            </header>

            <div className="mmc-dashboard__values-grid">
                <div className="mmc-card mmc-value-card">
                    <GameController size={32} weight="fill" className="ms-text-primary" />
                    <div className="mmc-value-card__content">
                        <h3>RETRO_GAMING</h3>
                        <p>Classic mechanics reimagined with technical aesthetics.</p>
                    </div>
                </div>
                <div className="mmc-card mmc-value-card">
                    <Trophy size={32} weight="fill" style={{ color: 'var(--mmc-clr-accent)' }} />
                    <div className="mmc-value-card__content">
                        <h3>HIGH_SCORES</h3>
                        <p>Local persistence to track your personal records.</p>
                    </div>
                </div>
                <div className="mmc-card mmc-value-card">
                    <Cpu size={32} weight="fill" />
                    <div className="mmc-value-card__content">
                        <h3>LIGHTWEIGHT</h3>
                        <p>Zero dependencies. Instant loading. Browser power.</p>
                    </div>
                </div>
            </div>

            <section className="mmc-dashboard__section">
                <div className="ms-flex" style={{ marginBottom: '24px' }}>
                    <Star size={20} weight="fill" className="ms-text-primary" />
                    <h2 className="ms-text-mono" style={{ fontSize: '1rem', fontWeight: 700 }}>ACTIVE_CABINETS</h2>
                </div>
                <div className="mmc-dashboard__games-grid">
                    {games.map(game => (
                        <div key={game.id} className="mmc-card mmc-game-card" onClick={() => onSelectGame(game.id)}>
                            <div className="mmc-game-card__header">
                                <span className="mmc-badge">{game.category || 'ARCADE'}</span>
                                <ArrowRight size={18} />
                            </div>
                            <h3 className="mmc-game-card__title">{game.display_name}</h3>
                            <p className="mmc-game-card__desc">{game.description}</p>
                        </div>
                    ))}
                </div>
            </section>

            <footer className="mmc-dashboard__footer">
                <div className="ms-text-mono">
                    <HardDrive size={12} /> ARCADE_SYSTEM // READY_FOR_PLAYERS // v1.0.0
                </div>
            </footer>

            <style>{`
                .mmc-dashboard { 
                    max-width: 1100px; 
                    margin: 0 auto; 
                    animation: ms-fade-in 0.5s ease;
                    padding-bottom: 64px;
                }
                
                .mmc-dashboard__hero {
                    margin-bottom: 64px;
                }

                .mmc-dashboard__title { 
                    font-size: clamp(2.5rem, 8vw, 4.5rem); 
                    line-height: 0.9; 
                    font-weight: 900; 
                    margin-bottom: 24px; 
                    text-transform: uppercase;
                }

                .mmc-dashboard__subtitle { 
                    font-size: clamp(1rem, 2vw, 1.25rem); 
                    opacity: 0.7; 
                    max-width: 550px; 
                    line-height: 1.5;
                }

                .mmc-dashboard__values-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                    gap: 24px;
                    margin-bottom: 80px;
                }

                .mmc-value-card {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .mmc-value-card h3 {
                    font-family: var(--mmc-font-mono);
                    font-size: 1rem;
                    margin-bottom: 4px;
                }

                .mmc-value-card p {
                    font-size: 0.85rem;
                    opacity: 0.6;
                    line-height: 1.4;
                }

                .mmc-dashboard__games-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
                    gap: 24px;
                }

                .mmc-game-card {
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                    border: 1px solid var(--mmc-clr-border);
                }

                .mmc-game-card:hover {
                    border-color: var(--mmc-clr-primary);
                    background: rgba(255,157,0,0.02);
                    transform: translateY(-4px);
                    box-shadow: 0 20px 40px rgba(0,0,0,0.3), 0 0 20px rgba(255,157,0,0.05);
                }

                .mmc-game-card__header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .mmc-game-card__title {
                    font-family: var(--mmc-font-mono);
                    font-size: 1.25rem;
                    font-weight: 800;
                }

                .mmc-game-card__desc {
                    font-size: 0.85rem;
                    opacity: 0.6;
                    line-height: 1.5;
                }

                .mmc-dashboard__footer {
                    margin-top: 100px;
                    padding-top: 24px;
                    border-top: 1px dashed var(--mmc-clr-border);
                    opacity: 0.4;
                }

                .ms-text-primary { color: var(--mmc-clr-primary); }
                .ms-text-mono { font-family: var(--mmc-font-mono); font-size: 0.75rem; text-transform: uppercase; }
                .ms-flex { display: flex; align-items: center; gap: 12px; }

                @keyframes ms-fade-in {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                @media (max-width: 768px) {
                    .mmc-dashboard__hero { margin-bottom: 40px; }
                    .mmc-dashboard__values-grid { margin-bottom: 48px; }
                    .mmc-game-card { padding: 20px; }
                }
            `}</style>
        </div>
    );
};

export default MmcDashboard;
